import type { VehicleDefectComparison, VehicleDefectProfile } from '../api/types'
import { formatNumber, formatPercent } from '../domain/format'
import {
  defectProfileDataNote,
  defectProfileScope,
  summariseDefects,
  type DefectRow,
} from '../domain/defectProfile'

/**
 * What vehicles of this model get pulled up on at the APK.
 *
 * Two things about this card are correctness, not layout.
 *
 * **It is about other cars.** Every figure is measured over the vehicles of this one's model (or,
 * on the live fallback, its type approval); nothing here was observed on the car in the report, whose own history is in the
 * inspection card above. The subtitle says what was measured and the footnote says whose cars they
 * were, and neither is optional — without them "15,4%" reads as a finding against this vehicle.
 *
 * **Afkeurpunten and adviespunten are separate lists and must stay separate.** An adviespunt is
 * wear that has not yet reached the limit and a reparatieadvies is a warning lamp that was lit;
 * both are recorded at inspections the vehicle **passed**. Merging them under one heading — or
 * under a word like "gebreken" — states something false, and on some models the advice outranks
 * every real defect: a 2015 Opel Astra carries the tyre-pressure warning at 10.3% of its
 * inspections, more than anything it actually fails on.
 *
 * Hence the two groups are visually distinct rather than merely stacked: the afkeurpunten take the
 * warning tone, the adviespunten a muted one, and each carries its own sentence of explanation.
 */
export function DefectProfileSection({ profile }: { profile: VehicleDefectProfile }) {
  const rejections = summariseDefects(profile.rejectionDefects)
  const advisories = summariseDefects(profile.advisoryDefects)
  const dataNote = defectProfileDataNote(profile)

  // Both lists are scaled against the same maximum so a 15% bar is the same width in either group.
  // Scaling each separately would make the Astra's advisory notes look like its worst failures.
  const widest = Math.max(...[...rejections, ...advisories].map((row) => row.sharePercent), 1)

  return (
    <div className="defects">
      {profile.thisVehicle && <VehicleComparison comparison={profile.thisVehicle} />}

      {rejections.length > 0 && (
        <DefectGroup
          rows={rejections}
          widest={widest}
          tone="rejection"
          title="Afkeurpunten"
          lead="Hierop werd dit model bij de APK afgekeurd. Het percentage is het aandeel keuringen waarbij dit is opgeschreven."
        />
      )}

      {advisories.length > 0 && (
        <DefectGroup
          rows={advisories}
          widest={widest}
          tone="advisory"
          title="Advies- en reparatiepunten"
          lead="Dit zijn op zichzelf géén redenen voor afkeuring. De keurmeester noteerde iets om in de gaten te houden: een band die nog net genoeg profiel heeft, of een waarschuwingslampje dat brandt."
        />
      )}

      <p className="card__footnote">
        {defectProfileScope(profile)} De keuringshistorie van dit voertuig zelf staat hierboven bij
        "Onderhoud en APK". Wat een auto nodig heeft hangt verder af van kilometerstand, gebruik en
        onderhoud, en dat staat niet in de gegevens van de RDW.
      </p>

      {dataNote && <p className="card__footnote">{dataNote}</p>}
    </div>
  )
}

/**
 * This car's own APK record set beside the model's.
 *
 * The rest of the card is about other vehicles, which is what makes it useful and also what makes
 * it hard to read: "15,4%" beside a plate means nothing until you know whether this particular car
 * is a poor example of a sound model or an ordinary example of a troublesome one. This block is the
 * only part of the endpoint that answers that, and it costs the API nothing — both figures were
 * already in hand.
 *
 * **Two rates, and no verdict.** There is deliberately no "beter"/"slechter", no score and no
 * colour that grades the car: a grade invites a reader to skip the figures that produced it, and
 * whatever weighting produced it would be invented. Both bars are drawn in the same neutral tone
 * for that reason — the comparison is the reader's to make.
 *
 * **The two rates are not quite like for like**, and the note says so. This vehicle's figure covers
 * every inspection the RDW holds for it, of whatever kind; the cohort's denominator is periodic
 * inspections only. On most vehicles those are nearly the same set, and "nearly" is exactly why
 * both counts travel with the rates instead of the rates travelling alone.
 */
function VehicleComparison({ comparison }: { comparison: VehicleDefectComparison }) {
  const widest = Math.max(comparison.defectsPerInspection, comparison.cohortDefectsPerInspection, 0.1)

  const bars = [
    {
      label: 'Deze auto',
      rate: comparison.defectsPerInspection,
      basis: `${formatNumber(comparison.rejectionDefects)} afkeurpunten over ${formatNumber(comparison.inspections)} ${
        comparison.inspections === 1 ? 'keuring' : 'keuringen'
      }`,
    },
    {
      label: 'Dit model',
      rate: comparison.cohortDefectsPerInspection,
      basis: `gemeten over ${formatNumber(comparison.cohortInspections)} keuringen`,
    },
  ]

  return (
    <section className="comparison">
      <h3 className="comparison__title">Deze auto naast het model</h3>
      <p className="comparison__lead">
        Afkeurpunten per keuring. Dit is het enige cijfer in dit onderdeel dat over deze auto zelf
        gaat.
      </p>

      <ul className="comparison__list">
        {bars.map((bar) => (
          <li className="comparison__item" key={bar.label}>
            <span className="comparison__name">
              {bar.label}
              <span className="comparison__basis">{bar.basis}</span>
            </span>
            <span className="comparison__bar" aria-hidden="true">
              <span className="comparison__bar-fill" style={{ width: `${Math.max((bar.rate / widest) * 100, 1.5)}%` }} />
            </span>
            <span className="comparison__rate">{formatNumber(bar.rate)}</span>
          </li>
        ))}
      </ul>

      {comparison.advisoryDefects > 0 && (
        <p className="comparison__advisory">
          Bij die keuringen {comparison.advisoryDefects === 1 ? 'is' : 'zijn'} daarnaast{' '}
          {formatNumber(comparison.advisoryDefects)}{' '}
          {comparison.advisoryDefects === 1 ? 'advies- of reparatiepunt' : 'advies- en reparatiepunten'} genoteerd. Dat
          zijn op zichzelf geen redenen voor afkeuring.
        </p>
      )}

      <p className="comparison__caveat">
        De twee cijfers zijn niet helemaal gelijksoortig: bij deze auto tellen alle geregistreerde
        keuringen mee, bij het model alleen de periodieke keuringen. Daarom staan de aantallen
        erbij.
      </p>
    </section>
  )
}

interface DefectGroupProps {
  rows: DefectRow[]
  widest: number
  tone: 'rejection' | 'advisory'
  title: string
  lead: string
}

function DefectGroup({ rows, widest, tone, title, lead }: DefectGroupProps) {
  return (
    <section className={`defects__group defects__group--${tone}`}>
      <h3 className="defects__title">{title}</h3>
      <p className="defects__lead">{lead}</p>

      <ul className="defects__list">
        {rows.map((row) => (
          <li className="defects__item" key={row.code}>
            <span className="defects__name">{row.label}</span>
            <span className="defects__bar" aria-hidden="true">
              {/* Scaled against the widest row across both groups, with a floor so a rare finding
                  is still a visible mark rather than nothing at all. */}
              <span
                className="defects__bar-fill"
                style={{ width: `${Math.max((row.sharePercent / widest) * 100, 1.5)}%` }}
              />
            </span>
            <span className="defects__count">
              {formatPercent(row.sharePercent)}
              <span className="defects__occurrences">{formatNumber(row.occurrences)}×</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
